import React, { useState, useEffect } from 'react';
import { KLOOK_AID, TRIP_AID, TRIP_SID, KKDAY_CID, sailyLink, radicalStorageLink } from '../constants';
import supabase from '../supabase';
import { residencyAllowed } from '../constants/residency';
import { hopegooCouponUrl } from '../constants/hopegoo';

const MERCHANT_META = {
  klook: { label:'Klook', color:'#ff5a00', bg:'#fff7ed', link: (city, country, aid) =>
    `https://www.klook.com/en-HK/search/result/?aid=${aid}&query=${encodeURIComponent(city||country||'')}` },
  trip:  { label:'Trip.com', color:'#003580', bg:'#eff6ff', link: (city) =>
    `https://www.trip.com/search/?keyword=${encodeURIComponent(city||'')}&Allianceid=${TRIP_AID}&SID=${TRIP_SID}` },
  kkday: { label:'KKday', color:'#7c3aed', bg:'#faf5ff', link: (city) =>
    `https://www.kkday.com/en-hk/search?q=${encodeURIComponent(city||'')}&cid=${KKDAY_CID}` },
  saily: { label:'Saily', color:'#0f172a', bg:'#f1f5f9', link: () => sailyLink() },
  radicalstorage: { label:'Radical Storage', color:'#0f9488', bg:'#f0fdfa', link: () => radicalStorageLink() },
  hopegoo: { label:'HopeGoo', color:'#1f9d55', bg:'#f0fdf4', link: (city, country, aid, code) => hopegooCouponUrl(code) },
  other: { label:'查看', color:'#475569', bg:'#f8fafc', link: () => '#' },
};

const CACHE_KEY = 'ec_coupons_v4'; // 優惠碼有增減或改分類時升版，清走訪客舊快取（v3：2026-10-21 換上 ECTRAVELHG）
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

// Classifies a coupon as 'city' (matches the trip's destination), 'global' (no city restriction), or null (irrelevant)
function couponScope(citiesStr, city, country) {
  if (!citiesStr || !citiesStr.trim()) return 'global';
  const keys = citiesStr.split(',').map(s => s.trim().toLowerCase());
  if (keys.includes('global')) return 'global';
  const search = `${city||''} ${country||''}`.toLowerCase();
  return keys.some(k => k && search.includes(k)) ? 'city' : null;
}

// hideMerchants：頁面上已有獨立橫幅或卡片的商戶（如 Saily、Radical Storage），列表不再重複顯示
// collapsible：預設收合成一行，點擊才展開完整列表（預訂頁用，避免畫面雜亂）
export const RelevantCoupons = ({ city, country, lang, hideMerchants = [], collapsible = false }) => {
  const [allCoupons, setAllCoupons] = useState([]);
  const [expanded, setExpanded]     = useState(null); // expanded coupon's id||code (unique across both groups)
  const [userResidency, setUserResidency] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [listOpen, setListOpen] = useState(false);

  useEffect(() => {
    const cached = (() => { try { const d = JSON.parse(sessionStorage.getItem(CACHE_KEY)); return d?.ts && Date.now()-d.ts < CACHE_TTL ? d.data : null; } catch{ return null; } })();
    if (cached) { setAllCoupons(cached); return; }
    supabase.from('coupons').select('*').eq('is_active', true).then(({ data, error }) => {
      if (error) console.error('[RelevantCoupons] fetch error:', error.message, error.code);
      if (data?.length) { setAllCoupons(data); try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch{} }
    });
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data:{ session } }) => setUserResidency(session?.user?.user_metadata?.residency_country || null));
  }, []);

  // 按香港日期判斷過期，避免 UTC 令昨天到期的碼在凌晨 0–8 點仍然顯示
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' });
  const daysLeft = (exp) => exp ? Math.ceil((new Date(exp + 'T23:59:59') - new Date()) / 86400000) : 999;
  const isEn = lang === 'en';

  const matched = allCoupons
    .map(c => ({ ...c, scope: couponScope(c.cities, city, country) }))
    .filter(c => !hideMerchants.includes(c.merchant))
    .filter(c => c.scope && residencyAllowed(c.resident_countries, userResidency) && (c.expiry_date ? c.expiry_date >= todayStr : true))
    .sort((a, b) => new Date(a.expiry_date||'2099') - new Date(b.expiry_date||'2099'))
    .map(c => ({
      ...c,
      title: isEn ? (c.name_en || c.name_zh) : (c.name_zh || c.name_en),
      desc:  isEn ? (c.desc_en  || c.desc_zh) : (c.desc_zh  || c.desc_en),
    }));

  // City-specific deals take priority; global ones fill the remaining slots (cap 8 total)
  const cityCoupons   = matched.filter(c => c.scope === 'city').slice(0, 8);
  const globalCoupons = matched.filter(c => c.scope === 'global').slice(0, Math.max(0, 8 - cityCoupons.length));
  const total = cityCoupons.length + globalCoupons.length;

  if (!total) return null;

  const renderCoupon = (c) => {
    const meta = MERCHANT_META[c.merchant] || MERCHANT_META.other;
    const link = meta.link(city, country, KLOOK_AID, c.code);
    const key = c.id || c.code;
    const isOpen = expanded === key;
    return (
      <div key={key}>
        <div onClick={()=>setExpanded(isOpen?null:key)}
          style={{ display:'flex', alignItems:'center', gap:10, background:'white', border:'1.5px solid #e2e8f0',
            borderRadius:isOpen?'12px 12px 0 0':12, padding:'10px 14px', cursor:'pointer', transition:'border-color 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.borderColor=meta.color}
          onMouseLeave={e=>e.currentTarget.style.borderColor=isOpen?meta.color+'80':'#e2e8f0'}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <span style={{ background:'#fef3c7', border:'1px dashed #f59e0b', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:900, color:'#92400e', fontFamily:'monospace', letterSpacing:0.5 }}>{c.code}</span>
              <span style={{ fontSize:12, fontWeight:900, color:'#dc2626' }}>{c.discount}</span>
              <span style={{ fontSize:11, color:'#64748b', fontWeight:600 }}>{c.title}</span>
            </div>
          </div>
          {/* 按下即複製優惠碼並經聯盟連結前往，避免用戶只抄碼而漏計佣金 */}
          <a href={link} target="_blank" rel="sponsored noopener noreferrer"
            onClick={e=>{ e.stopPropagation(); navigator.clipboard?.writeText(c.code).catch(()=>{}); setCopiedKey(key); setTimeout(()=>setCopiedKey(k=>k===key?null:k), 3000); }}
            style={{ background:meta.color, color:'white', border:'none', borderRadius:8, padding:'5px 12px',
              fontSize:11, fontWeight:700, cursor:'pointer', flexShrink:0, textDecoration:'none' }}>
            {copiedKey === key ? (isEn?'✓ Copied':'✓ 已複製') : (isEn?`Copy & go to ${meta.label}`:`複製並前往 ${meta.label}`)} →
          </a>
        </div>
        {isOpen && (
          <div style={{ background:meta.bg, border:`1.5px solid ${meta.color}40`, borderTop:'none', borderRadius:'0 0 12px 12px', padding:'10px 14px' }}>
            <p style={{ margin:'0 0 6px', fontSize:12, color:'#374151', lineHeight:1.6 }}>{c.desc}</p>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              {c.payment_method && <span style={{ fontSize:10, background:'#fef3c7', color:'#92400e', borderRadius:6, padding:'2px 8px', fontWeight:700 }}>{isEn?'Payment: ':' 付款：'}{c.payment_method.split('/')[isEn?1:0]||c.payment_method}</span>}
              {c.resident_countries && <span style={{ fontSize:10, background:'#eff6ff', color:'#1d4ed8', borderRadius:6, padding:'2px 8px', fontWeight:700 }}>{isEn?'Residents: ':'限居住地：'}{c.resident_countries}</span>}
              {c.expiry_date && <span style={{ fontSize:10, color:'#6b7280' }}>{isEn?`Expires: ${c.expiry_date} (${daysLeft(c.expiry_date)} days left)`:`有效至 ${c.expiry_date}（剩 ${daysLeft(c.expiry_date)} 天）`}</span>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const place = cityCoupons.length > 0 ? (city || country) : '';
  if (collapsible && !listOpen) {
    return (
      <button onClick={()=>setListOpen(true)}
        style={{ marginTop:16, width:'100%', display:'flex', alignItems:'center', gap:8, background:'#fffbeb', border:'1.5px dashed #f59e0b',
          borderRadius:12, padding:'10px 14px', cursor:'pointer', fontSize:13, fontWeight:800, color:'#92400e', textAlign:'left' }}>
        <span style={{ fontSize:15 }}>🏷️</span>
        <span style={{ flex:1 }}>
          {isEn
            ? `${total} promo code${total>1?'s':''} available${place?` for ${place}`:''}`
            : `${place?`${place} 有 `:''}${total} 個可用優惠碼`}
        </span>
        <span style={{ fontSize:12 }}>{isEn?'Show ▾':'展開 ▾'}</span>
      </button>
    );
  }

  return (
    <div style={{ marginTop:16 }}>
      <div onClick={collapsible ? ()=>setListOpen(false) : undefined}
        style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, cursor:collapsible?'pointer':'default' }}>
        <span style={{ fontSize:15 }}>🏷️</span>
        <p style={{ fontSize:14, fontWeight:900, color:'#0f172a', margin:0 }}>{isEn?'Promo Codes':'專屬優惠碼'}</p>
        <span style={{ fontSize:10, color:'#64748b', fontWeight:600 }}>· {isEn?`${total} available`:`${total} 個可用`}</span>
        {collapsible && <span style={{ marginLeft:'auto', fontSize:12, fontWeight:800, color:'#92400e' }}>{isEn?'Hide ▴':'收起 ▴'}</span>}
      </div>
      {/* 提示用戶在打開的頁面內下單，避免另開 App 導致聯盟追蹤失效 */}
      <p style={{ margin:'-4px 0 10px', fontSize:11, color:'#92400e', fontWeight:600, lineHeight:1.5 }}>
        💡 {isEn?'Please complete your booking on the page the button opens — it helps keep EC Travel free.':'請在按鈕打開的頁面內直接完成預訂，支持 EC Travel 繼續提供免費優惠資訊。'}
      </p>

      {cityCoupons.length > 0 && (
        <div style={{ marginBottom: globalCoupons.length ? 16 : 0 }}>
          <p style={{ fontSize:11, fontWeight:800, color:'#92400e', margin:'0 0 8px' }}>
            📍 {isEn ? `Exclusive to ${city || country}` : `${city || country} 專屬優惠`}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {cityCoupons.map(renderCoupon)}
          </div>
        </div>
      )}

      {globalCoupons.length > 0 && (
        <div>
          <p style={{ fontSize:11, fontWeight:800, color:'#64748b', margin:'0 0 8px' }}>
            🌍 {isEn ? 'Global Deals' : '全球通用優惠'}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {globalCoupons.map(renderCoupon)}
          </div>
        </div>
      )}
    </div>
  );
};
